/* import React, { useEffect, useState } from "react";
import { X, RotateCcw, Loader2 } from "lucide-react";
import { MeasureAPI } from "../services/api";
import { useSession } from "../context/SessionContext";

export interface WorkMeasurePopupItem {
  workMeasureNo?: number;
  WorkMeasureNo?: number;
  wmno?: number;
  WMNo?: number;

  workMeasure?: string | null;
  WorkMeasure?: string | null;

  value?: number | null;
  Value?: number | null;

  parameter?: string | null;
  Parameter?: string | null;

  [key: string]: unknown;
}

interface TakeoffWorkMeasurePopupProps {
  takeoffNo: number;
  rows: WorkMeasurePopupItem[];
  loading: boolean;
  error: string;
  onClose: () => void;
}

interface PopupRow {
  id: number;
  wmno: number;
  workMeasure: string;
  value: number | null;
  parameter: string;
  isEditing: boolean;
  isSaving: boolean;
  error: string;
}

const getWorkMeasureNo = (item: WorkMeasurePopupItem): number => {
  return Number(
    item.workMeasureNo ??
      item.WorkMeasureNo ??
      item.wmno ??
      item.WMNo ??
      0
  );
};

const getWorkMeasureName = (item: WorkMeasurePopupItem): string => {
  return String(item.workMeasure ?? item.WorkMeasure ?? "");
};

const getWorkMeasureValue = (
  item: WorkMeasurePopupItem
): number | null => {
  const rawValue = item.value ?? item.Value;

  if (rawValue === null || rawValue === undefined) {
    return null;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const getParameter = (item: WorkMeasurePopupItem): string => {
  const raw = item.parameter ?? item.Parameter;
  return raw == null || String(raw).trim() === ""
    ? "None"
    : String(raw);
};

const TakeoffWorkMeasurePopup: React.FC<
  TakeoffWorkMeasurePopupProps
> = ({
  takeoffNo,
  rows: items,
  loading,
  error,
  onClose,
}) => {
  const { sessionNo } = useSession();

  const [rows, setRows] = useState<PopupRow[]>([]);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setRows(
      items.map((item, index) => ({
        id: index,
        wmno: getWorkMeasureNo(item),
        workMeasure: getWorkMeasureName(item),
        value: getWorkMeasureValue(item),
        parameter: getParameter(item),
        isEditing: false,
        isSaving: false,
        error: "",
      }))
    );

    setNotice("");
  }, [items, takeoffNo]);

  const updateRow = (
    id: number,
    changes: Partial<PopupRow>
  ) => {
    setRows((current) =>
      current.map((row) =>
        row.id === id ? { ...row, ...changes } : row
      )
    );
  };

  const resetParameter = (id: number) => {
    setRows((current) =>
      current.map((row) =>
        row.id === id
          ? {
              ...row,
              parameter: "None",
              isEditing: true,
              error: "",
            }
          : row
      )
    );

    setNotice("");
  };

  const saveOnEnter = async (
    event: React.KeyboardEvent<HTMLInputElement>,
    row: PopupRow
  ) => {
    if (event.key !== "Enter") return;

    event.preventDefault();

    if (row.isSaving) return;

    const inputValue = event.currentTarget.value.trim();

    if (inputValue === "") {
      updateRow(row.id, {
        error: "Please enter a value.",
      });
      return;
    }

    const mval = Number(inputValue);

    if (!Number.isFinite(mval)) {
      updateRow(row.id, {
        error: "Enter a valid numeric value.",
      });
      return;
    }

    if (!row.wmno || row.wmno <= 0) {
      updateRow(row.id, {
        error: "Work Measure No. is missing or invalid.",
      });
      return;
    }

    if (sessionNo == null || sessionNo <= 0) {
      updateRow(row.id, {
        error: "Session number is missing. Please reopen the project.",
      });
      return;
    }

    updateRow(row.id, {
      isSaving: true,
      error: "",
    });

    try {
      await MeasureAPI.update({
        wmno: row.wmno,
        sno: sessionNo,
        mval,
        axn: "MeasureUpdate",
      });

      setRows((current) =>
        current.map((item) =>
          item.id === row.id
            ? {
                ...item,
                value: mval,
                parameter: "None",
                isEditing: false,
                isSaving: false,
                error: "",
              }
            : item
        )
      );

      setNotice("Work measure updated successfully.");
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update the work measure.";

      updateRow(row.id, {
        isSaving: false,
        isEditing: true,
        error: message,
      });

      setNotice("");
    }
  };

  return (
    <div
      className="twm-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="twm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="twm-title"
      >
        <header className="twm-header">
          <div>
            <h2 id="twm-title">Takeoff Work Measures — #{takeoffNo}</h2>
            <p>Reset a parameter to edit its work measure value.</p>
          </div>

          <button
            type="button"
            className="twm-close"
            onClick={onClose}
            aria-label="Close popup"
          >
            <X size={19} />
          </button>
        </header>

        <div className="twm-table-wrapper">
          <table className="twm-table">
            <thead>
              <tr>
                <th className="twm-number">S.No.</th>
                <th>Work Measure</th>
                <th>Parameter</th>
                <th>Value</th>
                <th className="twm-action-heading">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="twm-empty">
                    Loading work measures...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="twm-empty twm-error-message">
                    {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="twm-empty">
                    No work measures found.
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.wmno}-${row.id}`}>
                    <td className="twm-number">{index + 1}</td>

                    <td className="twm-work-measure">
                      {row.workMeasure || "—"}
                    </td>

                    <td>
                      <span
                        className={
                          row.parameter === "None"
                            ? "twm-parameter-none"
                            : "twm-parameter"
                        }
                      >
                        {row.parameter}
                      </span>
                    </td>

                    <td className="twm-value-cell">
                      {row.isEditing ? (
                        <div className="twm-value-editor">
                          <input
                            type="number"
                            step="any"
                            value={row.value ?? ""}
                            disabled={row.isSaving}
                            autoFocus={false}
                            aria-label={`Value for ${row.workMeasure}`}
                            aria-invalid={Boolean(row.error)}
                            className={
                              row.error
                                ? "twm-value-input twm-input-error"
                                : "twm-value-input"
                            }
                            onChange={(event) => {
                              const raw = event.target.value;

                              updateRow(row.id, {
                                value:
                                  raw === "" ? null : Number(raw),
                                error: "",
                              });
                            }}
                            onKeyDown={(event) =>
                              saveOnEnter(event, row)
                            }
                          />

                          {row.isSaving && (
                            <Loader2
                              className="twm-spinner"
                              size={16}
                            />
                          )}

                          {row.error && (
                            <span className="twm-error">
                              {row.error}
                            </span>
                          )}

                          <span className="twm-enter-hint">
                            Press Enter to update
                          </span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="twm-readonly-value"
                          onClick={() => {
                            if (row.parameter === "None") {
                              updateRow(row.id, {
                                isEditing: true,
                                error: "",
                              });
                            }
                          }}
                          title={
                            row.parameter === "None"
                              ? "Click to edit"
                              : "Reset the parameter before editing"
                          }
                        >
                          {row.value ?? "—"}
                        </button>
                      )}
                    </td>

                    <td className="twm-action-cell">
                      <button
                        type="button"
                        className="twm-reset-button"
                        disabled={row.isSaving}
                        onClick={() => resetParameter(row.id)}
                      >
                        <RotateCcw size={14} />
                        Reset Parameter
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {notice && (
          <div className="twm-success" role="status">
            {notice}
          </div>
        )}

        <footer className="twm-footer">
          <span>
            {rows.length} work measure{rows.length === 1 ? "" : "s"}
          </span>

          <button
            type="button"
            className="twm-done-button"
            onClick={onClose}
          >
            Close
          </button>
        </footer>
      </section>

      <style>{`
        .twm-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(15, 23, 42, 0.45);
          font-family: "Segoe UI", Arial, sans-serif;
          font-size: 13px;
          color: #303443;
        }

        .twm-modal {
          display: flex;
          flex-direction: column;
          width: min(1000px, 96vw);
          max-height: 85vh;
          overflow: hidden;
          background: #fff;
          border: 1px solid #dedfe5;
          border-radius: 8px;
          box-shadow: 0 18px 55px rgba(0, 0, 0, 0.18);
        }

        .twm-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 17px 20px;
          border-bottom: 1px solid #e7e8ec;
        }

        .twm-header h2 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #53538c;
        }

        .twm-header p {
          margin: 5px 0 0;
          color: #777b88;
          font-size: 12px;
        }

        .twm-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: 0;
          border-radius: 5px;
          background: transparent;
          color: #646979;
          cursor: pointer;
        }

        .twm-close:hover {
          background: #f0f1f5;
        }

        .twm-table-wrapper {
          overflow: auto;
          min-height: 100px;
        }

        .twm-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }

        .twm-table thead {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f7f8fa;
        }

        .twm-table th,
        .twm-table td {
          padding: 11px 13px;
          border-bottom: 1px solid #ececf0;
          text-align: left;
          vertical-align: top;
          font-size: 13px;
          font-weight: 400;
        }

        .twm-table th {
          color: #53538c;
          font-weight: 600;
          white-space: nowrap;
        }

        .twm-table tbody tr:hover {
          background: #fafbfe;
        }

        .twm-number {
          width: 55px;
          color: #747988;
          text-align: center !important;
        }

        .twm-work-measure {
          min-width: 180px;
          color: #303443;
        }

        .twm-parameter,
        .twm-parameter-none {
          display: inline-block;
          max-width: 220px;
          overflow-wrap: anywhere;
        }

        .twm-parameter {
          color: #4f6687;
        }

        .twm-parameter-none {
          color: #9296a2;
          font-style: italic;
        }

        .twm-value-cell {
          min-width: 145px;
        }

        .twm-readonly-value {
          padding: 2px 5px;
          border: 1px solid transparent;
          border-radius: 4px;
          background: transparent;
          color: #303443;
          font: inherit;
          text-align: left;
        }

        .twm-readonly-value:hover {
          border-color: #d7d9e2;
        }

        .twm-value-editor {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
        }

        .twm-value-input {
          box-sizing: border-box;
          width: 120px;
          min-height: 31px;
          padding: 5px 8px;
          border: 1px solid #aeb6cc;
          border-radius: 4px;
          background: #fff;
          color: #252b38;
          font: inherit;
          outline: none;
        }

        .twm-value-input:focus {
          border-color: #53538c;
          box-shadow: 0 0 0 2px rgba(83, 83, 140, 0.12);
        }

        .twm-value-input:disabled {
          background: #f3f4f6;
        }

        .twm-input-error {
          border-color: #d04444;
        }

        .twm-enter-hint {
          color: #9296a2;
          font-size: 10px;
        }

        .twm-error {
          max-width: 240px;
          color: #c03939;
          font-size: 11px;
          overflow-wrap: anywhere;
        }

        .twm-spinner {
          color: #53538c;
          animation: twm-spin 0.9s linear infinite;
        }

        @keyframes twm-spin {
          to {
            transform: rotate(360deg);
          }
        }

        .twm-action-heading {
          width: 150px;
        }

        .twm-action-cell {
          white-space: nowrap;
        }

        .twm-reset-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 7px 9px;
          border: 1px solid #d9dce6;
          border-radius: 4px;
          background: #fff;
          color: #53538c;
          font: inherit;
          cursor: pointer;
        }

        .twm-reset-button:hover:not(:disabled) {
          background: #f3f4fa;
          border-color: #aeb4d0;
        }

        .twm-reset-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .twm-empty {
          padding: 28px !important;
          color: #858997;
          text-align: center !important;
        }

        .twm-error-message {
          color: #c03939;
        }

        .twm-success {
          padding: 10px 20px;
          border-top: 1px solid #e4eadf;
          background: #f6faf4;
          color: #356c36;
        }

        .twm-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 20px;
          border-top: 1px solid #e7e8ec;
          color: #858997;
        }

        .twm-done-button {
          padding: 7px 16px;
          border: 1px solid #d9dce6;
          border-radius: 4px;
          background: #fff;
          color: #53538c;
          font: inherit;
          cursor: pointer;
        }

        .twm-done-button:hover {
          background: #f3f4fa;
        }

        @media (max-width: 650px) {
          .twm-overlay {
            padding: 8px;
          }

          .twm-modal {
            width: 100%;
            max-height: 92vh;
          }

          .twm-header,
          .twm-footer {
            padding: 12px;
          }

          .twm-table th,
          .twm-table td {
            padding: 9px;
          }
        }
      `}</style>
    </div>
  );
};

export default TakeoffWorkMeasurePopup;
 */
import React, { useEffect, useState } from "react";
import { X, RotateCcw, Loader2 } from "lucide-react";
import { MeasureAPI } from "../services/api";
import { useSession } from "../context/SessionContext";

export interface WorkMeasurePopupItem {
  workMeasureNo?: number;
  WorkMeasureNo?: number;
  wmno?: number;
  WMNo?: number;

  workMeasure?: string | null;
  WorkMeasure?: string | null;

  value?: number | null;
  Value?: number | null;

  parameter?: string | null;
  Parameter?: string | null;

  [key: string]: unknown;
}

interface TakeoffWorkMeasurePopupProps {
  takeoffNo: number;
  rows: WorkMeasurePopupItem[];
  loading: boolean;
  error: string;
  onClose: () => void;
}

interface PopupRow {
  id: number;
  wmno: number;
  workMeasure: string;
  value: number | null;
  parameter: string;
  isEditing: boolean;
  isSaving: boolean;
  error: string;
}

const getWorkMeasureNo = (item: WorkMeasurePopupItem): number => {
  return Number(
    item.workMeasureNo ??
      item.WorkMeasureNo ??
      item.wmno ??
      item.WMNo ??
      0
  );
};

const getWorkMeasureName = (item: WorkMeasurePopupItem): string => {
  return String(item.workMeasure ?? item.WorkMeasure ?? "");
};

const getWorkMeasureValue = (
  item: WorkMeasurePopupItem
): number | null => {
  const rawValue = item.value ?? item.Value;

  if (rawValue === null || rawValue === undefined) {
    return null;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const getParameter = (item: WorkMeasurePopupItem): string => {
  const raw = item.parameter ?? item.Parameter;
  return raw == null || String(raw).trim() === "" || Number(raw) === 0
    ? "None"
    : String(raw);
};

const TakeoffWorkMeasurePopup: React.FC<
  TakeoffWorkMeasurePopupProps
> = ({
  takeoffNo,
  rows: items,
  loading,
  error,
  onClose,
}) => {
  const { sessionNo } = useSession();

  const [rows, setRows] = useState<PopupRow[]>([]);

  useEffect(() => {
    setRows(
      items.map((item, index) => ({
        id: index,
        wmno: getWorkMeasureNo(item),
        workMeasure: getWorkMeasureName(item),
        value: getWorkMeasureValue(item),
        parameter: getParameter(item),
        isEditing: false,
        isSaving: false,
        error: "",
      }))
    );

  }, [items, takeoffNo]);

  const updateRow = (
    id: number,
    changes: Partial<PopupRow>
  ) => {
    setRows((current) =>
      current.map((row) =>
        row.id === id ? { ...row, ...changes } : row
      )
    );
  };

  const resetParameter = (id: number) => {
    setRows((current) =>
      current.map((row) =>
        row.id === id
          ? {
              ...row,
              parameter: "None",
              isEditing: true,
              error: "",
            }
          : row
      )
    );

  };

  const resetAllParameters = () => {
    setRows((current) =>
      current.map((row) => ({
        ...row,
        parameter: "None",
        isEditing: true,
        error: "",
      }))
    );
  };

  const saveOnEnter = async (
    event: React.KeyboardEvent<HTMLInputElement>,
    row: PopupRow
  ) => {
    if (event.key !== "Enter") return;

    event.preventDefault();

    if (row.isSaving) return;

    const inputValue = event.currentTarget.value.trim();

    if (inputValue === "") {
      updateRow(row.id, {
        error: "Please enter a value.",
      });
      return;
    }

    const mval = Number(inputValue);

    if (!Number.isFinite(mval)) {
      updateRow(row.id, {
        error: "Enter a valid numeric value.",
      });
      return;
    }

    if (!row.wmno || row.wmno <= 0) {
      updateRow(row.id, {
        error: "Work Measure No. is missing or invalid.",
      });
      return;
    }

    if (sessionNo == null || sessionNo <= 0) {
      updateRow(row.id, {
        error: "Session number is missing. Please reopen the project.",
      });
      return;
    }

    updateRow(row.id, {
      isSaving: true,
      error: "",
    });

    try {
      await MeasureAPI.update({
        wmno: row.wmno,
        sno: sessionNo,
        mval,
        axn: "MeasureUpdate",
      });

      setRows((current) =>
        current.map((item) =>
          item.id === row.id
            ? {
                ...item,
                value: mval,
                parameter: "None",
                isEditing: false,
                isSaving: false,
                error: "",
              }
            : item
        )
      );

    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update the work measure.";

      updateRow(row.id, {
        isSaving: false,
        isEditing: true,
        error: message,
      });

    }
  };

  return (
    <div
      className="twm-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="twm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="twm-title"
      >
        <header className="twm-header">
          <div>
            <h2 id="twm-title">Takeoff Work Measures — #{takeoffNo}</h2>
            <p>Reset a parameter to edit its work measure value.</p>
          </div>

          <div className="twm-header-actions">
            <button
              type="button"
              className="twm-reset-all-button"
              disabled={loading || rows.length === 0 || rows.some((row) => row.isSaving)}
              onClick={resetAllParameters}
            >
              <RotateCcw size={14} />
              Reset All
            </button>
            <button
              type="button"
              className="twm-close"
              onClick={onClose}
              aria-label="Close popup"
            >
              <X size={19} />
            </button>
          </div>
        </header>

        <div className="twm-table-wrapper">
          <table className="twm-table">
            <thead>
              <tr>
                <th className="twm-number">S.No.</th>
                <th>Work Measure</th>
                <th>Parameter</th>
                <th>Value</th>
                <th className="twm-action-heading">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="twm-empty">
                    Loading work measures...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="twm-empty twm-error-message">
                    {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="twm-empty">
                    No work measures found.
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.wmno}-${row.id}`}>
                    <td className="twm-number">{index + 1}</td>

                    <td className="twm-work-measure">
                      {row.workMeasure || "—"}
                    </td>

                    <td>
                      <span
                        className={
                          row.parameter === "None"
                            ? "twm-parameter-none"
                            : "twm-parameter"
                        }
                      >
                        {row.parameter}
                      </span>
                    </td>

                    <td className="twm-value-cell">
                      {row.isEditing ? (
                        <div className="twm-value-editor">
                          <input
                            type="number"
                            step="any"
                            value={row.value ?? ""}
                            disabled={row.isSaving}
                            autoFocus={false}
                            aria-label={`Value for ${row.workMeasure}`}
                            aria-invalid={Boolean(row.error)}
                            className={
                              row.error
                                ? "twm-value-input twm-input-error"
                                : "twm-value-input"
                            }
                            onChange={(event) => {
                              const raw = event.target.value;

                              updateRow(row.id, {
                                value:
                                  raw === "" ? null : Number(raw),
                                error: "",
                              });
                            }}
                            onKeyDown={(event) =>
                              saveOnEnter(event, row)
                            }
                          />

                          {row.isSaving && (
                            <Loader2
                              className="twm-spinner"
                              size={16}
                            />
                          )}

                          {row.error && (
                            <span className="twm-error">
                              {row.error}
                            </span>
                          )}

                          <span className="twm-enter-hint">
                            Press Enter to update
                          </span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="twm-readonly-value"
                          onClick={() => {
                            if (row.parameter === "None") {
                              updateRow(row.id, {
                                isEditing: true,
                                error: "",
                              });
                            }
                          }}
                          title={
                            row.parameter === "None"
                              ? "Click to edit"
                              : "Reset the parameter before editing"
                          }
                        >
                          {row.value ?? "—"}
                        </button>
                      )}
                    </td>

                    <td className="twm-action-cell">
                      {row.parameter !== "None" && (
                        <button
                          type="button"
                          className="twm-reset-button"
                          disabled={row.isSaving}
                          onClick={() => resetParameter(row.id)}
                        >
                          <RotateCcw size={14} />
                          Reset Parameter
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <footer className="twm-footer">
          <span>
            {rows.length} work measure{rows.length === 1 ? "" : "s"}
          </span>

          <button
            type="button"
            className="twm-done-button"
            onClick={onClose}
          >
            Close
          </button>
        </footer>
      </section>

      <style>{`
        .twm-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(15, 23, 42, 0.45);
          font-family: "Segoe UI", Arial, sans-serif;
          font-size: 13px;
          color: #303443;
        }

        .twm-modal {
          display: flex;
          flex-direction: column;
          width: min(1000px, 96vw);
          max-height: 85vh;
          overflow: hidden;
          background: #fff;
          border: 1px solid #dedfe5;
          border-radius: 8px;
          box-shadow: 0 18px 55px rgba(0, 0, 0, 0.18);
        }

        .twm-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 17px 20px;
          border-bottom: 1px solid #e7e8ec;
        }

        .twm-header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        .twm-reset-all-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 7px 10px;
          border: 1px solid #d9dce6;
          border-radius: 4px;
          background: #fff;
          color: #53538c;
          font: inherit;
          cursor: pointer;
          white-space: nowrap;
        }

        .twm-reset-all-button:hover:not(:disabled) {
          background: #f3f4fa;
          border-color: #aeb4d0;
        }

        .twm-reset-all-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .twm-header h2 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #53538c;
        }

        .twm-header p {
          margin: 5px 0 0;
          color: #777b88;
          font-size: 12px;
        }

        .twm-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: 0;
          border-radius: 5px;
          background: transparent;
          color: #646979;
          cursor: pointer;
        }

        .twm-close:hover {
          background: #f0f1f5;
        }

        .twm-table-wrapper {
          overflow: auto;
          min-height: 100px;
        }

        .twm-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }

        .twm-table thead {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f7f8fa;
        }

        .twm-table th,
        .twm-table td {
          padding: 11px 13px;
          border-bottom: 1px solid #ececf0;
          text-align: left;
          vertical-align: top;
          font-size: 13px;
          font-weight: 400;
        }

        .twm-table th {
          color: #53538c;
          font-weight: 600;
          white-space: nowrap;
        }

        .twm-table tbody tr:hover {
          background: #fafbfe;
        }

        .twm-number {
          width: 55px;
          color: #747988;
          text-align: center !important;
        }

        .twm-work-measure {
          min-width: 180px;
          color: #303443;
        }

        .twm-parameter,
        .twm-parameter-none {
          display: inline-block;
          max-width: 220px;
          overflow-wrap: anywhere;
        }

        .twm-parameter {
          color: #4f6687;
        }

        .twm-parameter-none {
          color: #9296a2;
          font-style: italic;
        }

        .twm-value-cell {
          min-width: 145px;
        }

        .twm-readonly-value {
          padding: 2px 5px;
          border: 1px solid transparent;
          border-radius: 4px;
          background: transparent;
          color: #303443;
          font: inherit;
          text-align: left;
        }

        .twm-readonly-value:hover {
          border-color: #d7d9e2;
        }

        .twm-value-editor {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
        }

        .twm-value-input {
          box-sizing: border-box;
          width: 120px;
          min-height: 31px;
          padding: 5px 8px;
          border: 1px solid #aeb6cc;
          border-radius: 4px;
          background: #fff;
          color: #252b38;
          font: inherit;
          outline: none;
        }

        .twm-value-input:focus {
          border-color: #53538c;
          box-shadow: 0 0 0 2px rgba(83, 83, 140, 0.12);
        }

        .twm-value-input:disabled {
          background: #f3f4f6;
        }

        .twm-input-error {
          border-color: #d04444;
        }

        .twm-enter-hint {
          color: #9296a2;
          font-size: 10px;
        }

        .twm-error {
          max-width: 240px;
          color: #c03939;
          font-size: 11px;
          overflow-wrap: anywhere;
        }

        .twm-spinner {
          color: #53538c;
          animation: twm-spin 0.9s linear infinite;
        }

        @keyframes twm-spin {
          to {
            transform: rotate(360deg);
          }
        }

        .twm-action-heading {
          width: 150px;
        }

        .twm-action-cell {
          white-space: nowrap;
        }

        .twm-reset-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 7px 9px;
          border: 1px solid #d9dce6;
          border-radius: 4px;
          background: #fff;
          color: #53538c;
          font: inherit;
          cursor: pointer;
        }

        .twm-reset-button:hover:not(:disabled) {
          background: #f3f4fa;
          border-color: #aeb4d0;
        }

        .twm-reset-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .twm-empty {
          padding: 28px !important;
          color: #858997;
          text-align: center !important;
        }

        .twm-error-message {
          color: #c03939;
        }

        .twm-success {
          padding: 10px 20px;
          border-top: 1px solid #e4eadf;
          background: #f6faf4;
          color: #356c36;
        }

        .twm-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 20px;
          border-top: 1px solid #e7e8ec;
          color: #858997;
        }

        .twm-done-button {
          padding: 7px 16px;
          border: 1px solid #d9dce6;
          border-radius: 4px;
          background: #fff;
          color: #53538c;
          font: inherit;
          cursor: pointer;
        }

        .twm-done-button:hover {
          background: #f3f4fa;
        }

        @media (max-width: 650px) {
          .twm-overlay {
            padding: 8px;
          }

          .twm-modal {
            width: 100%;
            max-height: 92vh;
          }

          .twm-header,
          .twm-footer {
            padding: 12px;
          }

          .twm-table th,
          .twm-table td {
            padding: 9px;
          }
        }
      `}</style>
    </div>
  );
};

export default TakeoffWorkMeasurePopup;
